import React from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import { useSelector }  from "react-redux";
import moment           from "moment";

import DownloadIcon     from "@mui/icons-material/Download";
import CheckCircleIcon  from "@mui/icons-material/CheckCircle";
import PersonIcon       from "@mui/icons-material/Person";
import PhoneIcon        from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ReceiptIcon      from "@mui/icons-material/Receipt";
import BadgeIcon        from "@mui/icons-material/Badge";

import { PDFDownloadLink }  from "@react-pdf/renderer";
import RefundPdf            from "./refundPdfView";
import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import { currencyFormatter, withoutSymbolFormatter } from "utilities/currencyFormatter";

/* ── Meta field ── */
const MetaField = ({ icon, label, value, textPri, textSec, accent }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
    <Box sx={{ color: accent, flexShrink: 0, mt: "2px", opacity: 0.7 }}>
      {React.cloneElement(icon, { sx: { fontSize: "15px" } })}
    </Box>
    <Box>
      <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: textSec, mb: "1px" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "13px", color: textPri }}>{value || "—"}</Typography>
    </Box>
  </Box>
);

const RefundViewForm = ({ viewData, handleClickApprove }) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";
  const user   = useSelector((s) => s.authReducer.user);

  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const {
    id: return_number,
    description,
    created_by,
    status: requisitionStatus,
    refunded_amount: grand_total,
    date_created: return_date,
    refund_products,
    related_receipt,
  } = viewData;

  const customer       = related_receipt?.related_invoice?.related_customer;
  const receipt_number = related_receipt?.receipt_number;
  const customerName   = customer
    ? `${customer.related_user.first_name} ${customer.related_user.last_name}`
    : "Walk-in customer";

  const columns = [
    { label: "#",                width: 40  },
    { label: "Product"                      },
    { label: "Unit"                         },
    { label: "Qty",              align: "center" },
    { label: "Purchase Amount",  align: "right"  },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>

      {/* ── Header ── */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: "0.75rem",
        p: "1rem 1.25rem",
        backgroundColor: "rgba(29,158,117,0.06)",
        border: `1px solid ${border}`,
        borderRadius: "10px",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box sx={{ width: "36px", height: "36px", borderRadius: "9px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
            <ReceiptIcon sx={{ fontSize: "19px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "17px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Refund Receipt
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "1px" }}>
              #{return_number}
            </Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: "20px", fontWeight: 500, color: accent }}>
          {currencyFormatter(parseFloat(grand_total))}
        </Typography>
      </Box>

      {/* ── Meta grid ── */}
      <Box sx={{
        backgroundColor: cardBg,
        border: `1px solid ${border}`,
        borderRadius: "10px",
        overflow: "hidden",
      }}>
        <Box sx={{ px: "1.25rem", py: "0.75rem", borderBottom: `1px solid ${border}`, backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.03)" }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: accent }}>
            Refund Details
          </Typography>
        </Box>
        <Box sx={{ p: "1rem 1.25rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.875rem 2rem" }}>
          <MetaField icon={<PersonIcon />}       label="Customer"         value={customerName}                           textPri={textPri} textSec={textSec} accent={accent} />
          <MetaField icon={<ReceiptIcon />}      label="Receipt No."      value={receipt_number}                         textPri={textPri} textSec={textSec} accent={accent} />
          <MetaField icon={<PhoneIcon />}        label="Phone"            value={customer?.related_user?.phone_number}   textPri={textPri} textSec={textSec} accent={accent} />
          <MetaField icon={<CalendarTodayIcon />} label="Refund Date"     value={moment(return_date).format("LL")}       textPri={textPri} textSec={textSec} accent={accent} />
          <MetaField icon={<BadgeIcon />}        label="Created By"       value={`${created_by?.first_name} ${created_by?.last_name}`} textPri={textPri} textSec={textSec} accent={accent} />
          {description && (
            <Box sx={{ gridColumn: "1 / -1" }}>
              <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: textSec, mb: "4px" }}>
                Description
              </Typography>
              <Typography sx={{ fontSize: "13px", color: textPri, lineHeight: 1.6 }}>{description}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Products table ── */}
      <ReportTable
        columns={columns}
        rows={refund_products ?? []}
        emptyMessage="No refund items found"
        renderRow={(row, index) => (
          <RRow key={index} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>
                {row.related_invoice_product.related_product.product_name}
              </Typography>
            </RCell>
            <RCell>{row.related_invoice_product.related_product_unit.related_business_product_unit.name}</RCell>
            <RCell align="center">{row.related_invoice_product.quantity}</RCell>
            <RCell align="right">{withoutSymbolFormatter(parseFloat(row.related_invoice_product.total))}</RCell>
          </RRow>
        )}
      />

      {/* ── Total row ── */}
      <Box sx={{
        display: "flex", justifyContent: "flex-end",
        p: "0.875rem 1.25rem",
        backgroundColor: cardBg,
        border: `1px solid ${border}`,
        borderRadius: "10px",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
          <Typography sx={{ fontSize: "13px", color: textSec }}>Total Refunded</Typography>
          <Typography sx={{ fontSize: "18px", fontWeight: 500, color: accent }}>
            {currencyFormatter(parseFloat(grand_total))}
          </Typography>
        </Box>
      </Box>

      {/* ── Actions ── */}
      <Box sx={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
        <PDFDownloadLink
          document={<RefundPdf viewData={viewData} printedBy={`${user.first_name} ${user.last_name}`} />}
          fileName={`Customer Refund #${return_number}`}
          style={{ textDecoration: "none" }}
        >
          <Button
            startIcon={<DownloadIcon sx={{ fontSize: "16px" }} />}
            sx={{
              fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem",
              borderRadius: "8px", textTransform: "none",
              border: `1px solid ${border}`, color: accent,
              backgroundColor: "rgba(29,158,117,0.06)",
              "&:hover": { backgroundColor: "rgba(29,158,117,0.12)" },
            }}
          >
            Download PDF
          </Button>
        </PDFDownloadLink>

        {requisitionStatus === 0 && (
          <Button
            onClick={handleClickApprove}
            startIcon={<CheckCircleIcon sx={{ fontSize: "16px" }} />}
            sx={{
              fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem",
              borderRadius: "8px", textTransform: "none",
              backgroundColor: accent, color: "#fff",
              "&:hover": { backgroundColor: "#0F6E56" },
            }}
          >
            Approve
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default RefundViewForm;